/**
 * Pantalla de edición masiva por Excel para Clientes y Proveedores. Es el mismo flujo de
 * Pages/Panel/Insumos/EdicionMasivaInsumos (filtros + vista previa, grupos de columnas,
 * descargar, borrar columnas, subir, revisar el diff y guardar en background), pero
 * parametrizado con un `config` para no repetirlo en las dos pantallas:
 *
 *   Pages/Panel/Clientes/EdicionMasivaClientes/EdicionMasivaClientes.js
 *   Pages/Panel/Proveedores/EdicionMasivaProveedores/EdicionMasivaProveedores.js
 *
 * Al backend sólo viajan los campos que cambiaron de cada renglón (ver edicionMasivaExcel.js).
 */
import { Component } from "react";
import { message, PageHeader, Spin, Upload, Row, Col, Alert, Switch, Checkbox, Collapse, Select } from "antd";
import { connect } from "react-redux";
import axios from "axios";
import { TableItem, ButtonItem } from "./Items";
import TableItemDinamico from "./TableItemDinamico";
import { UploadOutlined, DownloadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx-js-style";
import { encodeId, decodeId } from "./idEncoder";
import ModalProgreso from "./ModalProgreso";
import SeguidorProcesoMasivo from "./seguirProcesoMasivo";
import {
  aclararColor,
  valorExportado,
  evaluarCelda,
  erroresIdentidad,
  normalizarCp,
  descargarLibroEdicionMasiva,
  descargarLibroErrores,
} from "./edicionMasivaExcel";

const renderListaCampos = (campos) =>
  campos.length ? (
    <ul style={{ margin: 0, paddingLeft: 18, maxWidth: 260 }}>
      {campos.map((c) => (
        <li key={c}>{c}</li>
      ))}
    </ul>
  ) : (
    <span style={{ color: "#8c8c8c" }}>Sin campos configurados</span>
  );

const nombreParaMostrar = (e) =>
  (e?.persona
    ? e?.razonSocial
    : [e?.nombre, e?.apellidoPaterno, e?.apellidoMaterno].filter(Boolean).join(" ")) || "";

class EdicionMasivaEntidad extends Component {
  constructor(props) {
    super(props);
    const gruposVisibles = this.gruposVisibles();
    const filtrosExternos = {};
    (props.config.filtros || []).forEach((f) => {
      filtrosExternos[f.key] = [];
    });
    this.state = {
      loading: false,
      datos: null,
      entidadesMap: {},
      gruposSeleccionados: new Set(gruposVisibles.filter((g) => !g.fijo).map((g) => g.key)),
      filtrosExternos,
      previewIniciado: false,
      previewData: [],
      previewTotal: 0,
      previewLoading: false,
      archivosExcel: [],
      data: [],
      columnasPresentes: [],
      filasError: [],
      errores: 0,
      soloConCambios: true,
      sinIdError: false,
      erroresBackend: [],
      guardando: false,
      progreso: { procesados: 0, total: null },
    };
  }

  componentDidMount() {
    const { config } = this.props;
    // El guardado corre en el servidor por lotes y avisa por WebSocket, igual que la
    // edición masiva de insumos.
    this.seguidor = new SeguidorProcesoMasivo({
      empresaId: this.props.empresaId,
      headersToken: this.props.headersToken,
      onProgreso: (progreso) => this.setState({ guardando: true, progreso }),
      onFin: (resumen) => {
        this.setState({ guardando: false, progreso: { procesados: 0, total: null } });
        // Sin resumen el proceso terminó pero no dejó números: se cierra sin inventarlos.
        if (!resumen) return;
        const erroresBackend = resumen.errores || [];
        if (erroresBackend.length) {
          message.warning(
            `Se actualizaron ${resumen.numero ?? 0} ${config.plural}. Se omitieron ${erroresBackend.length} por errores.`
          );
          this.setState({ erroresBackend });
        } else {
          message.success(`Se actualizaron ${resumen.numero ?? 0} ${config.plural}`);
          this.atras();
        }
      },
      onError: (error) => {
        this.setState({ guardando: false, progreso: { procesados: 0, total: null } });
        message.error(error || `Hubo un error al actualizar los ${config.plural}`);
      },
    });
    this.seguidor.escuchar();

    this.setState({ loading: true });
    axios
      .post(config.endpoints.datos, { empresaId: this.props.empresaId }, { headers: this.props.headersToken })
      .then((res) => {
        if (res.data.success !== 2) {
          message.error("No se pudieron cargar los catálogos");
          this.setState({ loading: false });
          return;
        }
        this.setState({ datos: res.data, loading: false });
      })
      .catch(() => {
        message.error("No se pudieron cargar los catálogos");
        this.setState({ loading: false });
      });
  }

  componentWillUnmount() {
    if (this.seguidor) this.seguidor.detener();
  }

  atras() {
    this.props.history.goBack();
  }

  gruposVisibles() {
    const ocultos = new Set(this.props.gruposOcultos || []);
    return this.props.config.grupos.filter((g) => !ocultos.has(g.key));
  }

  // Columnas editables (todo lo que no es ID ni de sólo lectura) de los grupos visibles.
  specsEditables() {
    const visibles = new Set(this.gruposVisibles().flatMap((g) => g.columnas));
    return this.props.config.columnas.filter((s) => s.tipo !== "soloLectura" && visibles.has(s.col));
  }

  contexto() {
    const { config } = this.props;
    if (this._ctxDatos !== this.state.datos) {
      this._ctxDatos = this.state.datos;
      this._ctx = {
        catalogos: this.state.datos ? config.catalogos(this.state.datos) : {},
        direccionDe: config.direccionDe,
      };
    }
    return this._ctx;
  }

  grupoDeColumna(col) {
    return this.gruposVisibles().find((g) => g.columnas.includes(col)) || null;
  }

  consultarPreview(params) {
    // Ignora la consulta automática al montar.
    if (!this.state.previewIniciado) {
      this.setState({ previewLoading: false });
      return;
    }
    this.setState({ previewLoading: true });
    axios
      .post(
        this.props.config.endpoints.preview,
        { empresaId: this.props.empresaId, ...params },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({
            previewData: res.data.data || [],
            previewTotal: res.data.totalRecords || 0,
            previewLoading: false,
          });
        } else {
          this.setState({ previewData: [], previewTotal: 0, previewLoading: false });
        }
      })
      .catch(() => this.setState({ previewData: [], previewTotal: 0, previewLoading: false }));
  }

  actualizarFiltrosPreview(cambios) {
    const filtrosExternos = { ...this.state.filtrosExternos, ...cambios };
    this.setState({ filtrosExternos, previewIniciado: true }, () => {
      if (this.tableRefPreview) this.tableRefPreview.updateExternalFilters(filtrosExternos);
    });
  }

  mostrarTodosPreview() {
    this.setState({ previewIniciado: true }, () => {
      if (this.tableRefPreview) this.tableRefPreview.updateExternalFilters(this.state.filtrosExternos);
    });
  }

  pedirEntidades(filtros) {
    const { config } = this.props;
    return axios
      .post(
        config.endpoints.exportar,
        { empresaId: this.props.empresaId, ...filtros },
        { headers: this.props.headersToken }
      )
      .then((res) => (res.data.success === 2 ? res.data[config.claveExportar] || [] : null))
      .catch(() => null);
  }

  descargarExcel() {
    const { config } = this.props;
    this.setState({ loading: true });
    this.pedirEntidades(this.state.filtrosExternos).then((entidades) => {
      if (!entidades) {
        this.setState({ loading: false });
        message.error(`No se pudieron obtener los ${config.plural} a exportar`);
        return;
      }
      if (!entidades.length) {
        this.setState({ loading: false });
        message.warning(`No hay ${config.plural} para descargar con los filtros seleccionados`);
        return;
      }
      const entidadesMap = {};
      entidades.forEach((e) => {
        entidadesMap[String(e._id)] = e;
      });
      this.setState({ entidadesMap, loading: false }, () => this.generarExcel(entidades));
    });
  }

  generarExcel(entidades) {
    const { config } = this.props;
    const { gruposSeleccionados } = this.state;
    const ctx = this.contexto();
    const specPorCol = new Map(config.columnas.map((s) => [s.col, s]));

    const columnas = this.gruposVisibles()
      .filter((g) => g.fijo || gruposSeleccionados.has(g.key))
      .flatMap((g) => g.columnas.map((col) => ({ col, grupo: g })));

    const filas = entidades.map((e) =>
      columnas.map(({ col }) =>
        col === "ID" ? encodeId(String(e._id)) : valorExportado(specPorCol.get(col), e, ctx)
      )
    );

    const incluidas = new Set(columnas.map((c) => c.col));
    const catalogos = (config.hojasCatalogo || [])
      .filter((h) => h.columnas.some((c) => incluidas.has(c)))
      .map((h) => {
        const catalogo = ctx.catalogos[h.catalogo];
        return {
          nombre: h.nombre,
          encabezados: ["Nombre"],
          filas: (catalogo?.lista || []).map((item) => [catalogo.etiqueta(item)]),
        };
      });

    descargarLibroEdicionMasiva({
      hoja: config.hoja,
      columnas,
      filas,
      instrucciones: config.instrucciones,
      ejemplos: config.ejemplos,
      catalogos,
      nombreArchivo: config.nombreArchivo,
    });
  }

  onChangeArchivo(e) {
    this.setState({ loading: true });
    if (e.file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
      this.setState({ archivosExcel: [{ ...e.file, name: e.file.name, status: "uploading" }] });
      this.cargarArchivoExcel(e.file);
    } else {
      message.error("El archivo debe ser un archivo de Excel (.xlsx)");
      this.setState({ loading: false });
    }
  }

  cargarArchivoExcel(file) {
    const { config } = this.props;
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onloadend = (e) => {
      const workbook = XLSX.read(new Uint8Array(e.target.result), { type: "array" });
      const filas = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], {
        defval: "",
        range: 1,
      });
      this.asegurarEntidadesMap(filas).then(() => {
        const result = this.procesarFilas(filas);
        this.setState({
          data: result.data,
          columnasPresentes: result.columnasPresentes,
          errores: result.filasError.length,
          filasError: result.filasError,
          sinIdError: result.sinIdError,
          archivosExcel: [{ ...file, name: file.name, status: "success" }],
          loading: false,
          erroresBackend: [],
        });
        if (result.ignoradas.length) {
          message.warning(
            `Se ignoraron las columnas ${result.ignoradas.join(", ")}: no tiene permiso para editarlas.`
          );
        }
        if (result.sinIdError) {
          message.error(
            "El archivo no contiene la columna ID. Descargue el Excel desde este módulo e intente nuevamente."
          );
        } else if (result.filasError.length) {
          message.warning(
            `Se detectaron ${result.filasError.length} errores. Descargue el archivo de errores para revisarlos.`
          );
        } else {
          const conCambios = result.data.filter((d) => d._hayCambios).length;
          message.success(
            `Archivo leído: ${conCambios} ${config.singular}(s) con cambios de ${result.data.length} leídos.`
          );
        }
      });
    };
  }

  // entidadesMap sólo se llena al descargar el Excel. Si el archivo trae algún ID que no está
  // (página recargada, archivo de otra sesión o descargado con otro filtro), se vuelve a pedir
  // el set completo sin filtros antes de procesar.
  asegurarEntidadesMap(filas) {
    const { entidadesMap } = this.state;
    const faltaAlguno = filas.some((fila) => {
      const encodedId = String(fila["ID"] || "").trim();
      if (!encodedId) return false;
      const decodedId = decodeId(encodedId);
      return !decodedId || !Object.prototype.hasOwnProperty.call(entidadesMap, decodedId);
    });
    if (!faltaAlguno) return Promise.resolve();

    return this.pedirEntidades({}).then((entidades) => {
      if (!entidades) {
        message.error(`No se pudieron obtener los ${this.props.config.plural} para validar el archivo`);
        return;
      }
      const nuevoMap = {};
      entidades.forEach((e) => {
        nuevoMap[String(e._id)] = e;
      });
      return new Promise((resolve) => this.setState({ entidadesMap: nuevoMap }, resolve));
    });
  }

  procesarFilas(filas) {
    const { config } = this.props;
    const vacio = { data: [], columnasPresentes: [], filasError: [], sinIdError: false, ignoradas: [] };
    if (!filas.length) return vacio;

    const primeraFila = filas[0];
    if (!("ID" in primeraFila)) return { ...vacio, sinIdError: true };

    const editables = this.specsEditables();
    const columnasPresentes = editables.filter((s) => s.col in primeraFila);
    const nombresEditables = new Set(editables.map((s) => s.col));
    const ignoradas = config.columnas
      .filter((s) => s.tipo !== "soloLectura" && !nombresEditables.has(s.col) && s.col in primeraFila)
      .map((s) => s.col);

    const ctx = this.contexto();
    const { entidadesMap } = this.state;
    const data = [];
    const filasError = [];

    for (const fila of filas) {
      const encodedId = String(fila["ID"] || "").trim();
      const decodedId = encodedId ? decodeId(encodedId) : null;
      const entidad = decodedId ? entidadesMap[decodedId] : null;
      if (!encodedId || !entidad) {
        filasError.push({ ...fila, Errores: !encodedId ? "ID vacío" : `ID no encontrado: ${encodedId}` });
        continue;
      }

      const cambios = {};
      const direccion = {};
      const display = {};
      const columnasCambiadas = [];
      const filaErrores = [];

      for (const spec of columnasPresentes) {
        const r = evaluarCelda(spec, fila[spec.col], entidad, ctx);
        if (r.error) {
          filaErrores.push(r.error);
          continue;
        }
        display[`_d_${spec.col}`] = r.display;
        if (!r.cambio) continue;
        if (spec.tipo === "direccion") direccion[spec.campo] = r.valor;
        else cambios[spec.campo] = r.valor;
        columnasCambiadas.push(spec.col);
      }

      // CP y colonia se resuelven juntos en el servidor, así que viajan los dos.
      if ("cp" in direccion || "colonia" in direccion) {
        const actual = config.direccionDe(entidad);
        if (!("cp" in direccion)) direccion.cp = normalizarCp(actual.cp);
        if (!("colonia" in direccion)) {
          // Sin la columna Colonia, un CP nuevo toma su única colonia (o pide indicarla).
          direccion.colonia = columnasPresentes.some((s) => s.col === "Colonia") ? actual.colonia : "";
        }
      }

      filaErrores.push(...erroresIdentidad(entidad, cambios));
      if (config.validarFila) filaErrores.push(...config.validarFila(entidad, cambios, ctx));

      if (filaErrores.length) {
        filasError.push({ ...fila, Errores: filaErrores.join("; ") });
        continue;
      }

      const payload = { ...cambios };
      if (Object.keys(direccion).length) payload.direccion = direccion;

      data.push({
        _id: decodedId,
        _codigo: entidad.codigo,
        _nombre: nombreParaMostrar(entidad),
        _cambios: columnasCambiadas,
        _hayCambios: columnasCambiadas.length > 0,
        _payload: payload,
        ...display,
      });
    }

    return { data, columnasPresentes: columnasPresentes.map((s) => s.col), filasError, sinIdError: false, ignoradas };
  }

  descargarArchivoErrores() {
    if (!this.state.filasError.length) return;
    descargarLibroErrores(this.state.filasError, `errores_${this.props.config.nombreArchivo}`);
  }

  onFinish() {
    const { config } = this.props;
    const dataConCambios = this.state.data.filter((d) => d._hayCambios);
    if (!dataConCambios.length) {
      message.warning("No hay cambios que guardar");
      return;
    }

    this.setState({ loading: true, erroresBackend: [] });
    axios
      .post(
        config.endpoints.editar,
        {
          empresaId: this.props.empresaId,
          columnas: this.state.columnasPresentes,
          data: dataConCambios.map((row) => ({ [config.idCampo]: row._id, ...row._payload })),
        },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        this.setState({ loading: false });
        if (res.data.success !== 2) {
          // Incluye el rechazo por candado: ya hay otra operación masiva en curso.
          message.error(res.data.message || `Hubo un error al actualizar los ${config.plural}`);
          return;
        }
        if (res.data.total === 0) {
          // No hubo nada que escribir: el backend responde en directo, sin proceso en background.
          this.setState({ erroresBackend: res.data.errores || [] });
          message.warning(
            `No hubo ${config.plural} que actualizar. Se omitieron ${(res.data.errores || []).length}.`
          );
          return;
        }
        this.seguidor.seguir(res.data.procesoId);
        this.setState({ guardando: true, progreso: { procesados: 0, total: res.data.total ?? null } });
      })
      .catch(() => {
        this.setState({ loading: false });
        message.error(`Hubo un error al actualizar los ${config.plural}`);
      });
  }

  render() {
    const { config } = this.props;
    const { data, columnasPresentes, errores, sinIdError, soloConCambios, erroresBackend, gruposSeleccionados, datos } =
      this.state;
    const gruposVisibles = this.gruposVisibles();

    const totalConCambios = data.filter((d) => d._hayCambios).length;
    const dataMostrar = soloConCambios ? data.filter((d) => d._hayCambios) : data;

    const renderCelda = (col, grupo) => (_, record) => {
      const changed = record._cambios.includes(col);
      const colorGrupo = grupo?.color;
      return (
        <span
          style={{
            backgroundColor: changed ? (colorGrupo ? `#${aclararColor(colorGrupo, 0.72)}` : "#fff3cd") : undefined,
            borderLeft: changed && colorGrupo ? `3px solid #${colorGrupo}` : undefined,
            padding: changed ? "2px 6px" : undefined,
            borderRadius: changed ? 4 : undefined,
            display: "inline-block",
          }}
        >
          {record[`_d_${col}`] ?? ""}
        </span>
      );
    };

    const indiceGrupo = (g) => (g ? gruposVisibles.indexOf(g) : gruposVisibles.length);
    const columnasTabla = columnasPresentes
      .map((col) => ({ col, grupo: this.grupoDeColumna(col) }))
      .sort((a, b) => indiceGrupo(a.grupo) - indiceGrupo(b.grupo))
      .map(({ col, grupo }) => ({
        title: col,
        key: col,
        width: 150,
        onHeaderCell: () => (grupo ? { style: { backgroundColor: `#${aclararColor(grupo.color, 0.85)}` } } : {}),
        render: renderCelda(col, grupo),
      }));

    const columns = [
      { title: "Código", dataIndex: "_codigo", key: "_codigo", fixed: "left", width: 90 },
      { title: "Nombre", dataIndex: "_nombre", key: "_nombre", fixed: "left", width: 220 },
      ...columnasTabla,
    ];

    const etiquetaEntidad = (id) => {
      const e = this.state.entidadesMap[String(id)];
      return e ? `${e.codigo} - ${nombreParaMostrar(e)}` : id;
    };

    const filtros = config.filtros || [];

    return (
      <>
        <PageHeader
          onBack={this.atras.bind(this)}
          title={config.titulo}
          style={{ border: "1px solid rgb(235, 237, 240)", backgroundColor: "white", marginBottom: 10 }}
          extra={
            this.state.filasError.length ? (
              <ButtonItem onClick={this.descargarArchivoErrores.bind(this)}>Descargar Errores</ButtonItem>
            ) : null
          }
        />
        <div className="card-background" style={{ paddingTop: 5 }}>
          <Spin spinning={this.state.loading} tip="Cargando, espere por favor...">
            <Alert
              style={{ marginBottom: 16, marginTop: 10 }}
              type="info"
              showIcon
              message="Instrucciones"
              description={
                <ol style={{ margin: 0, paddingLeft: 20 }}>
                  <li>
                    Filtre (opcional) para acotar qué {config.plural} va a exportar. Revise la vista
                    previa antes de descargar.
                  </li>
                  <li>Elija qué grupos de datos quiere editar y descargue el Excel.</li>
                  <li>
                    <b>Borre las columnas que NO desee modificar.</b> Solo se actualizarán las
                    columnas que queden en el archivo. La columna Codigo es solo para identificar.
                  </li>
                  <li>
                    La columna <b>ID</b> es obligatoria y no debe eliminarse ni modificarse. Tampoco
                    borre las dos filas de encabezado.
                  </li>
                  <li>Suba el archivo modificado y revise la vista previa antes de guardar.</li>
                </ol>
              }
            />
            {filtros.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 8, fontWeight: 500 }}>
                  Filtra qué {config.plural} vas a exportar (opcional)
                </div>
                <Row gutter={12}>
                  {filtros.map((f) => (
                    <Col span={24 / filtros.length} key={f.key}>
                      <Select
                        mode="multiple"
                        allowClear
                        placeholder={f.placeholder}
                        style={{ width: "100%" }}
                        value={this.state.filtrosExternos[f.key]}
                        onChange={(vals) => this.actualizarFiltrosPreview({ [f.key]: vals })}
                        options={datos ? f.opciones(datos) : []}
                        optionFilterProp="label"
                      />
                    </Col>
                  ))}
                </Row>
              </div>
            )}
            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>
                Vista previa de lo que se va a descargar
                {this.state.previewIniciado && ` (${this.state.previewTotal} ${config.plural})`}
              </div>
              {this.state.previewIniciado ? (
                <TableItemDinamico
                  ref={(ref) => {
                    this.tableRefPreview = ref;
                  }}
                  onRef={(ref) => {
                    this.tableRefPreview = ref;
                  }}
                  size="small"
                  columns={config.columnasPreview}
                  dataSource={this.state.previewData}
                  totalRecords={this.state.previewTotal}
                  loading={this.state.previewLoading}
                  pagination={false}
                  rowKey="_id"
                  onFetchData={this.consultarPreview.bind(this)}
                />
              ) : (
                <Alert
                  type="info"
                  showIcon
                  message={`Elige un filtro arriba, o muestra todos los ${config.plural} para ver la vista previa.`}
                  action={
                    <ButtonItem size="small" onClick={this.mostrarTodosPreview.bind(this)}>
                      Mostrar todos
                    </ButtonItem>
                  }
                />
              )}
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>¿Qué datos quieres editar?</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-start" }}>
                {gruposVisibles.map((g) => (
                  <div
                    key={g.key}
                    style={{
                      flex: "1 1 170px",
                      minWidth: 170,
                      display: "flex",
                      flexDirection: "column",
                      background: "#fafafa",
                      border: "1px solid #eaeaea",
                      borderTop: `3px solid #${g.color}`,
                      borderRadius: 6,
                      overflow: "hidden",
                    }}
                  >
                    <div style={{ padding: "8px 12px 4px" }}>
                      {g.fijo ? (
                        <Checkbox checked disabled>
                          {g.label}
                        </Checkbox>
                      ) : (
                        <Checkbox
                          checked={gruposSeleccionados.has(g.key)}
                          onChange={(e) => {
                            const nuevo = new Set(gruposSeleccionados);
                            if (e.target.checked) nuevo.add(g.key);
                            else nuevo.delete(g.key);
                            this.setState({ gruposSeleccionados: nuevo });
                          }}
                        >
                          {g.label}
                        </Checkbox>
                      )}
                    </div>
                    <Collapse ghost size="small">
                      <Collapse.Panel header="Ver campos" key={g.key}>
                        {renderListaCampos(g.columnas)}
                      </Collapse.Panel>
                    </Collapse>
                  </div>
                ))}
              </div>
            </div>
            <Row style={{ marginBottom: 16, alignItems: "center" }}>
              <Col span={12}>
                <ButtonItem
                  type="primary"
                  icon={<DownloadOutlined />}
                  disabled={!datos}
                  onClick={this.descargarExcel.bind(this)}
                >
                  Descargar Excel
                </ButtonItem>
              </Col>
              <Col span={12}>
                <Upload
                  showUploadList={{ showRemoveIcon: false, showPreviewIcon: false }}
                  fileList={this.state.archivosExcel}
                  customRequest={this.onChangeArchivo.bind(this)}
                  accept=".xlsx"
                  disabled={!datos}
                >
                  <ButtonItem icon={<UploadOutlined />} disabled={!datos}>
                    Subir Excel modificado
                  </ButtonItem>
                </Upload>
              </Col>
            </Row>

            {sinIdError && (
              <Alert
                type="error"
                showIcon
                message="El archivo no contiene la columna ID. Descargue el Excel desde este módulo y vuelva a intentarlo."
                style={{ marginBottom: 16 }}
              />
            )}

            {(data.length > 0 || errores > 0) && !sinIdError && (
              <>
                <Alert
                  type={errores > 0 ? "warning" : "success"}
                  showIcon
                  style={{ marginBottom: 12 }}
                  message={
                    <>
                      Se detectaron <b>{totalConCambios}</b> {config.singular}(s) con cambios en las
                      columnas: <b>{columnasPresentes.join(", ")}</b>.
                      {errores > 0 && (
                        <>
                          {" "}
                          Se omitieron <b>{errores}</b> fila(s) con errores; descargue el archivo de
                          errores para revisarlas.
                        </>
                      )}
                    </>
                  }
                />
                {erroresBackend.length > 0 && (
                  <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message={`El servidor rechazó ${erroresBackend.length} ${config.singular}(s):`}
                    description={
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {erroresBackend.map((e, idx) => (
                          <li key={idx}>
                            <b>{etiquetaEntidad(e.id)}</b>: {e.motivo}
                          </li>
                        ))}
                      </ul>
                    }
                  />
                )}
                <div
                  style={{
                    marginBottom: 8,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>
                    <Switch
                      checked={soloConCambios}
                      onChange={(v) => this.setState({ soloConCambios: v })}
                      size="small"
                    />{" "}
                    Mostrar solo con cambios ({totalConCambios} de {data.length})
                  </span>
                  {totalConCambios > 0 && (
                    <ButtonItem type="primary" onClick={this.onFinish.bind(this)}>
                      Guardar cambios en {totalConCambios}{" "}
                      {totalConCambios !== 1 ? config.plural : config.singular}
                    </ButtonItem>
                  )}
                </div>
                <TableItem
                  size="small"
                  dataSource={dataMostrar}
                  columns={columns}
                  scroll={{ x: "max-content" }}
                  rowKey="_id"
                />
              </>
            )}
          </Spin>
        </div>
        <ModalProgreso
          visible={this.state.guardando}
          titulo="Guardando cambios"
          descripcion={`No cierres esta ventana: el proceso sigue en el servidor, pero perderías el avance en pantalla. Los ${config.plural} se escriben por lotes para no saturar la base.`}
          procesados={this.state.progreso.procesados}
          total={this.state.progreso.total}
          unidad={config.plural}
        />
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(EdicionMasivaEntidad);
